import { useState, useRef } from "react";
import { Scrollama, Step } from "react-scrollama";
import IntroViz from "./IntroViz";
import Toggle from "../Toggle";
import TextBox from "../TextBox";
import {
  SectionContainer,
  ScrollingContainer,
  StepContainer,
  SectionHeader,
} from "../SharedUI";
import { useContainerDimensions } from "../../hooks/useContainerDimensions";
import styled from "@emotion/styled";

const IntroScrollingContainer = styled(ScrollingContainer)`
  padding: 2em 4em 40vh 2em;
  box-sizing: border-box;
`;

export default function Introduction() {
  const [currentStep, setCurrentStep] = useState<
    | ""
    | "init"
    | "problem-1-header"
    | "intro"
    | "country-A-pov"
    | "country-B-pov"
    | "discrepancy"
    | "discrepancy2"
    | "toggle"
    | "scale"
  >("");
  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useContainerDimensions(containerRef);

  type StepKey =
    | ""
    | "init"
    | "problem-1-header"
    | "intro"
    | "country-A-pov"
    | "country-B-pov"
    | "discrepancy"
    | "discrepancy2"
    | "toggle"
    | "scale";

  const onStepEnter = ({ data }: { data: StepKey }) => {
    setCurrentStep(data);
  };

  const visualizationWidth = dimensions.width;
  const visualizationHeight = dimensions.height;

  return (
    <SectionContainer>
      <IntroViz
        currentStep={currentStep}
        width={visualizationWidth}
        height={visualizationHeight}
      />
      <IntroScrollingContainer ref={containerRef}>
        <Scrollama onStepEnter={onStepEnter}>
          <Step data={"init"}>
            <StepContainer style={{ marginTop: "20vh", marginBottom: "10vh" }}>
              <TextBox isActive={currentStep === "init"}>
                <div>
                  <p>
                    <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                      Country A
                    </span>{" "}
                    and{" "}
                    <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                      Country B
                    </span>{" "}
                    are trading partners. In theory, their reported trade data
                    should align. In practice, it rarely does.
                  </p>{" "}
                  <p>
                    This discrepancy highlights two persistent challenges in
                    international trade data: (1) how countries report trade,
                    and (2) how products are mapped across classification
                    systems. This site explains both issues, and how the Growth
                    Lab handles them.
                  </p>
                </div>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"problem-1-header"}>
            <StepContainer>
              <TextBox isActive={currentStep === "problem-1-header"}>
                <SectionHeader style={{ margin: 0 }}>
                  1. The Reporting Problem
                </SectionHeader>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"intro"}>
            <StepContainer>
              <TextBox isActive={currentStep === "intro"}>
                <p>
                  In 2024, both{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  and{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>{" "}
                  submitted their bilateral trade statistics to UN Comtrade for
                  the calendar year 2023. Despite their shared transactions,
                  their reported trades with each other did not align.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"country-A-pov"}>
            <StepContainer>
              <TextBox isActive={currentStep === "country-A-pov"}>
                <p>
                  For example,{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  reported that they exported $603 million to{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>
                  .
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"country-B-pov"}>
            <StepContainer>
              <TextBox isActive={currentStep === "country-B-pov"}>
                <p>
                  Meanwhile,{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>{" "}
                  reported that they imported $1.1 billion from{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>
                  .
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"discrepancy"}>
            <StepContainer>
              <TextBox isActive={currentStep === "discrepancy"}>
                <p>
                  Both countries are reporting the same flow of goods. Yet{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  reported{" "}
                  <span style={{ color: "#D669BF", fontWeight: 600 }}>
                    $497 million less
                  </span>{" "}
                  than{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>
                  .
                </p>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"discrepancy2"}>
            <StepContainer>
              <TextBox isActive={currentStep === "discrepancy2"}>
                <p>
                  If{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>{" "}
                  reports imports{" "}
                  <span style={{ color: "#D669BF", fontWeight: 600 }}>
                    82% higher
                  </span>{" "}
                  than{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  reports exports, what is the true value of trade transacted
                  between the two countries?
                </p>
              </TextBox>
            </StepContainer>
          </Step>

          <Step data={"toggle"}>
            <StepContainer style={{ minHeight: "auto", margin: "1em 0" }}>
              <TextBox isActive={currentStep === "toggle"} isToggle={true}>
                <Toggle label="How much discrepancy do we expect?">
                  <p>
                    Some amount of trade discrepancy is expected. For example,
                    import values will include the cost of insurance and freight
                    (CIF) paid by the importing country, which can inflate
                    reported imports by up to 20% compared to export values.
                    Before we mirror trade, we correct for CIF from the reported
                    import value by estimating CIF using a gravity model
                    regression on distance and whether the countries share a
                    border. The aim of this correction is to eliminate “correct”
                    discrepancies caused by reporting standards, leaving the
                    remaining discrepancy reflecting genuine reporting
                    differences.
                  </p>
                </Toggle>
              </TextBox>
            </StepContainer>
          </Step>
          <Step data={"scale"}>
            <StepContainer>
              <TextBox isActive={currentStep === "scale"}>
                <p>
                  Now, scale this example of{" "}
                  <span style={{ color: "#4b8b51ff", fontWeight: 600 }}>
                    Country A
                  </span>{" "}
                  and{" "}
                  <span style={{ color: "#4686a3ff", fontWeight: 600 }}>
                    Country B
                  </span>{" "}
                  to all countries and their trading partners. The discrepancies
                  compound.
                </p>
              </TextBox>
            </StepContainer>
          </Step>
        </Scrollama>
      </IntroScrollingContainer>
    </SectionContainer>
  );
}
